import {
  VulnerabilitiesResponse,
  VulnerabilityFindingDetail,
  OccurrencesResponse,
  VulnComment,
  VulnCommentsResponse,
  VulnTimelineResponse,
} from '../../app/modules/types/vulnerabilities'
import { VulOptionsType } from '../../app/modules/types/filters'
import { apiClient } from '../client'

export const vulnerabilitiesApi = {
  getAll: async (params?: string): Promise<VulnerabilitiesResponse> => {
    const url = params ? `/vulnerabilities/?${params}` : '/vulnerabilities/'
    const { data } = await apiClient.get(url)
    return data
  },

  getById: async (findingId: string): Promise<VulnerabilityFindingDetail> => {
    const { data } = await apiClient.get(`/vulnerabilities/${findingId}/`)
    return data
  },

  /**
   * Paginated evidence rows (§4b). The detail response already embeds the first
   * page (capped at 100), so this exists to page the REMAINDER when
   * `occurrence_count` exceeds what the embed carried — it is not the primary
   * source. Newest-first with a stable id tiebreak.
   *
   * `ids` restricts to specific occurrence ids (≤100); malformed ids are
   * silently ignored and ids from another finding/org return nothing.
   */
  getOccurrences: async (
    findingId: string,
    params: { page?: number; pageSize?: number; ids?: string[] } = {},
  ): Promise<OccurrencesResponse> => {
    const search = new URLSearchParams()
    if (params.page != null) search.set('page', String(params.page))
    if (params.pageSize != null) search.set('page_size', String(params.pageSize))
    if (params.ids?.length) search.set('ids', params.ids.join(','))
    const qs = search.toString()
    const url = qs
      ? `/vulnerabilities/${findingId}/occurrences/?${qs}`
      : `/vulnerabilities/${findingId}/occurrences/`
    const { data } = await apiClient.get(url)
    return data
  },

  getFilterOptions: async (): Promise<VulOptionsType> => {
    const { data } = await apiClient.get('/vulnerabilities/filter/')
    if (!data || typeof data !== 'object') return data
    // Drop null-named options at the boundary (unchanged from legacy; guards the
    // new `status` facet too). Non-array entries (created_at_range) pass through.
    const sanitized: Record<string, unknown> = {}
    for (const [category, value] of Object.entries(data)) {
      sanitized[category] = Array.isArray(value)
        ? value.filter((item) => item?.name != null)
        : value
    }
    return sanitized as VulOptionsType
  },

  getFiltered: async (params?: string): Promise<VulnerabilitiesResponse> => {
    const url = params ? `/vulnerabilities/filter/?${params}` : '/vulnerabilities/filter/'
    const { data } = await apiClient.get(url)
    return data
  },

  getComments: async (findingId: string, params?: string): Promise<VulnCommentsResponse> => {
    const url = params
      ? `/vulnerabilities/${findingId}/comments/?${params}`
      : `/vulnerabilities/${findingId}/comments/`
    const { data } = await apiClient.get(url)
    return data
  },

  addComment: async (
    findingId: string,
    payload: { body: string; parent_id?: string },
  ): Promise<VulnComment> => {
    const { data } = await apiClient.post(`/vulnerabilities/${findingId}/comments/`, payload)
    return data
  },

  editComment: async (
    findingId: string,
    commentId: string,
    body: string,
  ): Promise<VulnComment> => {
    const { data } = await apiClient.patch(
      `/vulnerabilities/${findingId}/comments/${commentId}/`,
      { body },
    )
    return data
  },

  deleteComment: async (findingId: string, commentId: string): Promise<void> => {
    await apiClient.delete(`/vulnerabilities/${findingId}/comments/${commentId}/`)
  },

  getTimeline: async (findingId: string, params?: string): Promise<VulnTimelineResponse> => {
    const url = params
      ? `/vulnerabilities/${findingId}/timeline/?${params}`
      : `/vulnerabilities/${findingId}/timeline/`
    const { data } = await apiClient.get(url)
    return data
  },

  delete: async (findingId: string): Promise<void> => {
    await apiClient.delete(`/vulnerabilities/${findingId}/`)
  },
}
